import { useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Lock, Unlock, KeyRound, Loader2 } from "lucide-react";

export default function UsersPage() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ roleCodes: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/v1/users");
      setRows(r.data.data.items);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get("/v1/tenants").then((r) => setTenants(r.data.data.items)).catch(() => {});
    api.get("/v1/roles").then((r) => setRoles(r.data.data)).catch(() => {});
  }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/v1/users", { ...form, roleCodes: form.roleCodes });
      toast.success("User created");
      setOpen(false); setForm({ roleCodes: [] }); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const action = async (id, act) => {
    try {
      if (act === "reset-password") {
        const pw = window.prompt("New password (min 12 chars):");
        if (!pw) return;
        await api.patch(`/v1/users/${id}/reset-password`, { newPassword: pw });
      } else {
        await api.patch(`/v1/users/${id}/${act}`);
      }
      toast.success("Done"); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const columns = [
    { key: "email", header: "Email" },
    { key: "name", header: "Name", render: (u) => `${u.firstName} ${u.lastName}` },
    { key: "roleCodes", header: "Roles", render: (u) => (u.roleCodes || []).map((r) => <Badge key={r} color="teal">{r}</Badge>) },
    { key: "tenantId", header: "Tenant", render: (u) => (u.isSuperAdmin ? <Badge color="amber">Platform</Badge> : (u.tenantId ? <span className="font-mono text-xs">{u.tenantId.slice(0, 8)}</span> : "—")) },
    { key: "status", header: "Status", render: (u) => <Badge color={u.status === "Active" ? "green" : u.status === "Locked" ? "red" : "slate"}>{u.status}</Badge> },
    {
      key: "_a", header: "",
      render: (u) => (
        <div className="flex items-center justify-end gap-1">
          {hasPermission("USER.LOCK") && u.status === "Active" && !u.isSuperAdmin && (
            <button onClick={() => action(u.id, "lock")} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Lock"><Lock className="h-4 w-4" /></button>
          )}
          {hasPermission("USER.UNLOCK") && u.status === "Locked" && (
            <button onClick={() => action(u.id, "unlock")} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-500" title="Unlock"><Unlock className="h-4 w-4" /></button>
          )}
          {hasPermission("USER.RESET_PASSWORD") && (
            <button onClick={() => action(u.id, "reset-password")} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Reset password"><KeyRound className="h-4 w-4" /></button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="users-page">
      <PageHeader title="User Management" subtitle="Platform & tenant users"
        actions={hasPermission("USER.CREATE") && (
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-primary text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="create-user-btn">
            <Plus className="h-4 w-4" /> New User
          </button>
        )} />
      <DataTable columns={columns} rows={rows} loading={loading} testid="users-table" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="user-create-dialog">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Create User</h3>
            <div className="grid grid-cols-2 gap-3">
              {[["firstName", "First Name"], ["lastName", "Last Name"], ["email", "Email"], ["mobile", "Mobile"]].map(([k, l]) => (
                <div key={k}>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{l}</label>
                  <input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid={`user-${k}`} />
                </div>
              ))}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Password (min 12)</label>
                <input type="password" value={form.password || ""} onChange={(e) => set("password", e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="user-password" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tenant</label>
                <select value={form.tenantId || ""} onChange={(e) => set("tenantId", e.target.value || null)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="user-tenant">
                  <option value="">Platform (no tenant)</option>
                  {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                <select value={(form.roleCodes || [])[0] || ""} onChange={(e) => set("roleCodes", e.target.value ? [e.target.value] : [])} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="user-role">
                  <option value="">Select role…</option>
                  {roles.map((r) => <option key={r.roleCode} value={r.roleCode}>{r.roleName}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50">Cancel</button>
              <button onClick={create} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-white font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="user-create-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
